import { Controller } from '@hotwired/stimulus'

const DIGIT_RE = /\D/g

export default class extends Controller {
  static targets = ['digit', 'fullCode', 'submit', 'error', 'errorMessage']

  static values = { networkError: String }

  connect () {
    this.refreshSubmit()
  }

  async submit (event) {
    event.preventDefault()
    this.hideError()
    this.submitTarget.disabled = true

    try {
      const response = await fetch(this.element.action, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
            ?.content,
        },
        body: new FormData(this.element),
      })

      if (response.ok) {
        // Navigate the frame for real so the JS bundle re-evaluates in a fresh
        // realm and Stimulus reboots — a document.write() would keep the same
        // realm, the ESM bundle would not re-run, and iframe-messenger on the
        // confirmation page would never connect (so success would never post).
        const { redirect_to: redirectTo } = await response.json()
        window.location.assign(redirectTo)
        return
      }

      if (response.status === 422) {
        const { error } = await response.json()
        this.showError(error)
      } else {
        this.showError(this.networkErrorValue)
      }
    } catch {
      this.showError(this.networkErrorValue)
    }

    this.resetDigits()
  }

  onInput (event) {
    const input = event.target
    const sanitized = input.value.replace(DIGIT_RE, '').slice(0, 1)

    if (input.value !== sanitized) {
      input.value = sanitized
    }

    if (sanitized) {
      this.focusNext(input)
    }

    this.syncFullCode()
    this.refreshSubmit()
  }

  onKeydown (event) {
    if (event.key === 'Backspace' && !event.target.value) {
      const previous = this.previousDigit(event.target)
      if (previous) {
        event.preventDefault()
        previous.focus()
        previous.value = ''
        this.syncFullCode()
        this.refreshSubmit()
      }
    }
  }

  onPaste (event) {
    const text = (event.clipboardData || window.clipboardData).getData('text')
    const digits = text.replace(DIGIT_RE, '').slice(0, this.digitTargets.length)

    if (!digits) return

    event.preventDefault()
    this.digitTargets.forEach((input, index) => {
      input.value = digits[index] || ''
    })

    const lastFilledIndex = Math.min(digits.length, this.digitTargets.length) - 1
    if (lastFilledIndex >= 0) {
      this.digitTargets[lastFilledIndex].focus()
    }

    this.syncFullCode()
    this.refreshSubmit()
  }

  focusNext (currentInput) {
    const index = this.digitTargets.indexOf(currentInput)
    const next = this.digitTargets[index + 1]
    if (next) next.focus()
  }

  previousDigit (currentInput) {
    const index = this.digitTargets.indexOf(currentInput)
    return this.digitTargets[index - 1]
  }

  syncFullCode () {
    if (!this.hasFullCodeTarget) return
    const code = this.digitTargets.map(input => input.value).join('')
    this.fullCodeTarget.value = code
  }

  refreshSubmit () {
    if (!this.hasSubmitTarget) return
    const allFilled = this.digitTargets.every(input => input.value.length === 1)
    this.submitTarget.disabled = !allFilled
  }

  showError (message) {
    if (!this.hasErrorTarget) return
    this.errorMessageTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError () {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.add('hidden')
  }

  resetDigits () {
    this.digitTargets.forEach(input => { input.value = '' })
    this.syncFullCode()
    this.refreshSubmit()
    this.digitTargets[0]?.focus()
  }
}
